import { useEffect, useState } from "react";
import { IconLink, IconUpload } from "@tabler/icons-react";
import s from "../partnerPortal.module.css";
import { Panel, SectionTitle } from "./shared";
import {
  partnerGetBranding,
  partnerGetProfile,
  partnerUpdateBranding,
  partnerUploadLogo,
} from "../../../services/partnerCalls";
import { DEFAULT_ERROR_MESSAGE } from "../../../constants";
import { errorHandler, toastSuccess } from "../../../utils";
import { convertToBase64 } from "../../../utils/functions";
import { usePartnerContext } from "../../../context/partnerContext";
import { Spinner } from "../../../components/UI/Spinner/Spinner";

const ACCENTS = [
  "#1e3a8a",
  "#2dd4bf",
  "#7c3aed",
  "#db2777",
  "#ea580c",
  "#059669",
];
const PROVIDERS = ["Greenhouse", "Lever", "Workable"];

export default function Settings() {
  const { profileData, loading, fetchPartnerProfile } = usePartnerContext();

  const [brand, setBrand] = useState("ITM Africa");
  const [accent, setAccent] = useState("#1e3a8a");
  const [logoUrl, setLogoUrl] = useState("");
  const [file, setFile] = useState(null);
  const [brandingLoading, setBrandingLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  const initials = brand.slice(0, 2).toUpperCase();

  const [branding, setBranding] = useState(null);

  const normalizeColor = (color) => {
    if (!color || typeof color !== "string") {
      return ACCENTS[0];
    }

    return color.trim().toLowerCase();
  };

  const handleBranding = async () => {
    setBrandingLoading(true);
    try {
      const response = await partnerUpdateBranding({
        accentColor: accent,
        brandName: brand,
        // the API accepts an https URL or no logoUrl at all, not ""
        ...(logoUrl ? { logoUrl } : {}),
      });

      if (response?.data?.data) {
        fetchBranding();
        fetchPartnerProfile();
        toastSuccess("Branding updated successfully");
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        errorHandler(error?.response?.data?.message || DEFAULT_ERROR_MESSAGE);
      }
    } finally {
      setBrandingLoading(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setLogoUploading(true);
      let base64Image = "";
      if (file) {
        base64Image = await convertToBase64(file);
        const res = await partnerUploadLogo({
          logo: base64Image,
        });
        if (res?.data?.data) {
          setLogoUrl(res?.data?.data?.logoUrl || "");
        }
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        errorHandler(error?.response?.data?.message || DEFAULT_ERROR_MESSAGE);
      }
    } finally {
      setLogoUploading(false);
      e.target.value = "";
    }
  };

  const fetchBranding = async () => {
    try {
      const res = await partnerGetBranding();

      const data = res?.data?.data;

      if (data) {
        setBranding(data);

        const apiAccent = normalizeColor(data?.accentColor);

        setAccent(apiAccent);
        setBrand(data?.brandName || "");
        setLogoUrl(data?.logoUrl || "");
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        errorHandler(error.response.data.message || DEFAULT_ERROR_MESSAGE);
      }
    }
  };

  useEffect(() => {
    fetchBranding();
  }, []);

  return (
    <div>
      <SectionTitle
        title="Settings"
        sub="Your white-label branding"
      />

      {/* Branding */}
      <Panel className={s.pg5}>
        <div className={`${s.blockTitle} ${s.pg130}`}>
          Branding
        </div>
        <div className={s.pg131}>
          Applied to candidate test pages and the emails Awasource sends on your
          behalf.
        </div>

        <div className={s.grid2even}>
          <div>
            <label className={s.label}>Brand name</label>
            <input
              className={s.input}
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
            />

            <label className={`${s.label} ${s.pg132}`}>
              Accent colour
            </label>
            <div className={s.pg133}>
              {ACCENTS?.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Accent ${c}`}
                  onClick={() => setAccent(c)}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: c,
                    border:
                      accent === c ? "2px solid #fff" : "2px solid transparent",
                    cursor: "pointer",
                  }}
                />
              ))}
            </div>

            <label className={`${s.label} ${s.pg132}`}>
              Logo
            </label>
            <div className={s.pg76}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  background: logoUrl ? "#fff" : accent,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 800,
                  fontSize: 15,
                  overflow: "hidden",
                }}
              >
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="Logo"
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                ) : (
                  initials
                )}
              </div>
              <input
                id="logoUrl"
                type="file"
                hidden
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={handleFileChange}
              />
              <label
                htmlFor="logoUrl"
                className={`${s.btnGhost} ${s.pg134}`}
              >
                {" "}
                <IconUpload
                  size={13}
                  style={{ verticalAlign: "-2px", marginRight: 5 }}
                />
                {logoUploading ? (
                  <Spinner text="Logo uploading..." />
                ) : logoUrl ? (
                  "Logo uploaded"
                ) : (
                  "Upload logo (PNG, JPEG, SVG or WEBP)"
                )}
              </label>
            </div>
          </div>

          <div>
            <label className={s.label}>Preview — candidate test header</label>
            <div className={s.pg135}
            >
              <div className={s.pg136}
              >
                <div className={s.pg38}>
                  <div
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 7,
                      background: logoUrl ? "#fff" : accent,
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 800,
                      fontSize: 11,
                      overflow: "hidden",
                    }}
                  >
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt="Logo"
                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                    ) : (
                      initials
                    )}
                  </div>
                  <span className={s.pg137}
                  >
                    {brand}
                  </span>
                </div>
                <span className={s.pg138}>
                  Powered by <b className={s.pg139}>Awasource</b>
                </span>
              </div>
              <div className={s.pg140}>
                <div className={s.pg141}
                >
                  Business Analyst assessment
                </div>
                <div className={s.pg142}>
                  Show {brand} how you think under pressure.
                </div>
              </div>
            </div>
            <div className={`${s.note} ${s.pg4}`}>
              The "Powered by Awasource" footer is fixed on standard plans.
            </div>
          </div>
        </div>

        <div className={s.pg143}>
          <button
            type="button"
            className={s.btnSolid}
            onClick={() => handleBranding()}
            disabled={brandingLoading}
          >
            {brandingLoading ? <Spinner text="Saving..." /> : "Save branding"}
          </button>
        </div>
      </Panel>

      {/* Notifications */}
      <Panel>
        <div className={s.pg144}>
          <div className={`${s.blockTitle} ${s.pg145}`}>Notifications</div>
          <span className={`${s.pill} ${s.pg156}`}>Coming soon</span>
        </div>
        <div className={s.pg146}>
          Email alerts for low balance, webhook failures and a weekly usage
          summary are on our roadmap — they aren't sent yet, so there's
          nothing to configure here today.
        </div>
      </Panel>

      {/* ATS */}
      <Panel className={s.pg154}>
        <div className={s.pg155}>
          <div className={`${s.blockTitle} ${s.pg145}`}>
            Integrations · Applicant Tracking System
          </div>
          <span className={`${s.pill} ${s.pg156}`}>Coming soon</span>
        </div>
        <div className={s.pg157}>
          Pulling job specs and candidate CVs directly from your ATS into
          Candidate Fit isn't available yet.
        </div>

        <div className={s.pg164}>
          {PROVIDERS.map((p) => (
            <div className={s.pg165} key={p}>
              <IconLink size={20} color="#8898b5" />
              <div className={s.pg166}>{p}</div>
              <button type="button" className={`${s.btnGhost} ${s.pg167}`} disabled>
                Connect
              </button>
            </div>
          ))}
        </div>

        <div className={s.note}>
          When this ships, connection will be via OAuth or a read-only API
          key — Awasource would pull open reqs (job specs) and applicant CVs,
          with scoring run on our side and results posted back by webhook.
        </div>
      </Panel>
    </div>
  );
}
